import { createMemo, createRoot, createSignal } from 'solid-js';
import { haversineMeters } from '~/lib/geo';
import { geolocation } from '~/lib/geolocation';
import { HEAT_MAX } from '~/lib/resolution';
import type { HackBand } from '~/lib/resolution';

/**
 * Heat — the global risk/tension system. Rises when hacks fail, hardens
 * subsequent targets (via the resolution HEAT HOOK), and decays over real time
 * faster as the player moves away from where the heat was generated.
 *
 * Representation: an **event list**. Each hack outcome that draws heat pushes an
 * event recording its origin coords, tier, and a `remaining` amount. The global
 * heat is the (capped) sum of all events' remaining amounts. This model is what
 * makes spatial decay and per-tier stickiness natural, and it reserves the
 * origin data future features need (surveillance sources, proximity clustering,
 * high-heat lockout). Decay is an incremental integrator: each tick subtracts
 * `rate(distance, tier) × dt` from each event's remaining (monotonic — heat
 * never rises from movement; distance only changes the decay *rate*).
 */

// ── Heat gain by outcome band (tier-scaled) ────────────────────────────────
export const HEAT_GAIN: Record<HackBand, number> = {
  clean: 0,
  partial: 6,
  fail: 18,
  badfail: 35,
};
/** Higher-tier targets draw more heat per band: gain ×= 1 + (tier-1)×scale.
 *  Tuning pass (prompt 23): 0.25 → 0.5, so a tier-4 failure spikes ≈2.5× a
 *  tier-1 one ("bigger orgs, more security contacts"). Base band gains above
 *  are unchanged — build rate felt right; only the tier-weighting steepened. */
export const HEAT_GAIN_TIER_SCALE = 0.5;

// ── Decay (time + distance + per-tier stickiness) ──────────────────────────
// Standing-still clear time = gain × stickiness[tier] / base (linear decay).
/** Base heat cleared per second, at the origin (distance 0), tier 1.
 *  Tuning pass (prompt 23): 0.75 → 0.18. Field data showed decay was far too
 *  fast (a tier-3 fail cleared in ~75s); slowing the base makes heat linger
 *  long enough to shape decisions. */
export const HEAT_DECAY_BASE_PER_SEC = 0.18;
/** Distance acceleration: rate ×= 1 + distance_m × factor.
 *  Tuning pass (prompt 23): 0.005 → 0.0025, so moving away helps but is not an
 *  escape hatch: ×1.5 at 200 m, ×2.0 at 400 m (was ~×2–3 / an effective reset). */
export const HEAT_DECAY_DISTANCE_FACTOR = 0.0025;
/** Per-tier stickiness divides the decay rate (higher value = slower clear).
 *  Tuning pass (prompt 23): EASED at the high end so the now-steeper gain
 *  scaling doesn't overshoot the target clear-time windows. The high-tier
 *  "lingers longer" feel now comes from the much larger gain magnitude, not
 *  from slow decay; tier-1/2 keep a little stickiness so minor overreaches are
 *  a noticeable (not trivial) tax. Tuned (with the gains and base) so standing
 *  still: tier-4 badfail ~4.9 min, tier-3 badfail ~3.2 min, tier-1/2 fail
 *  ~1.7–2.0 min. */
export const HEAT_TIER_STICKINESS: Record<number, number> = { 1: 1.0, 2: 0.8, 3: 0.5, 4: 0.6 };
/** Drop events below this remaining amount. */
const HEAT_EPSILON = 0.01;
/** Decay tick interval for the live store. */
const HEAT_TICK_MS = 1500;

// ── Range labels (HUD) ─────────────────────────────────────────────────────
export type HeatLabel = 'LOW' | 'ELEVATED' | 'HIGH' | 'CRITICAL';
export function heatLabel(value: number): HeatLabel {
  if (value <= 25) return 'LOW';
  if (value <= 50) return 'ELEVATED';
  if (value <= 80) return 'HIGH';
  return 'CRITICAL';
}

export interface HeatEvent {
  id: string;
  amount: number; // original gain (for reference)
  remaining: number; // current undecayed contribution
  origin: { lat: number; lng: number };
  tier: number; // 1–4, governs stickiness
  lastTickAt: number; // epoch ms of the last decay step
}

export type PlayerPos = { lat: number; lng: number } | null;

/** Tier-scaled heat gain for an outcome band. */
export function heatGainFor(band: HackBand, tier: number): number {
  return HEAT_GAIN[band] * (1 + (tier - 1) * HEAT_GAIN_TIER_SCALE);
}

/** PURE decay core: advance every event to `now`, subtracting
 *  rate(distance, tier) × elapsed from its remaining amount, and drop spent
 *  events. Distance is measured to the CURRENT player position (null → 0,
 *  i.e. on-origin / pre-fix). Monotonic: remaining never increases. Injectable
 *  `now`/`playerPos` make it testable without wall-clock or GPS. */
export function tickDecay(events: HeatEvent[], now: number, playerPos: PlayerPos): HeatEvent[] {
  const out: HeatEvent[] = [];
  for (const e of events) {
    const dtSec = Math.max(0, (now - e.lastTickAt) / 1000);
    const dist = playerPos ? haversineMeters(playerPos.lat, playerPos.lng, e.origin.lat, e.origin.lng) : 0;
    const stickiness = HEAT_TIER_STICKINESS[e.tier] ?? 1;
    const rate = (HEAT_DECAY_BASE_PER_SEC * (1 + dist * HEAT_DECAY_DISTANCE_FACTOR)) / stickiness;
    const remaining = Math.max(0, e.remaining - rate * dtSec);
    if (remaining > HEAT_EPSILON) out.push({ ...e, remaining, lastTickAt: now });
  }
  return out;
}

/** PURE: the current global heat from an event list (capped at HEAT_MAX). */
export function totalHeat(events: HeatEvent[]): number {
  const sum = events.reduce((s, e) => s + e.remaining, 0);
  return Math.min(HEAT_MAX, sum);
}

/** PURE: drain `amount` of heat from the event list, OLDEST events first
 *  (fully-drained events drop out). Used by the reputation-management service to
 *  lower heat by a purchased amount; integrates with decay because it just
 *  reduces event `remaining` (the decay tick keeps working on what's left). */
export function drainHeat(events: HeatEvent[], amount: number): HeatEvent[] {
  let toDrain = amount;
  const out: HeatEvent[] = [];
  for (const e of events) {
    if (toDrain <= 0) {
      out.push(e);
    } else if (e.remaining <= toDrain) {
      toDrain -= e.remaining; // fully drained → dropped
    } else {
      out.push({ ...e, remaining: e.remaining - toDrain });
      toDrain = 0;
    }
  }
  return out;
}

/** Module-level heat singleton. The pure core (tickDecay/totalHeat) is wrapped
 *  with a live decay timer + the geolocation singleton for the player position.
 *  Persistence-ready: state is the plain `events` list (step 6 can serialize it,
 *  re-stamping `lastTickAt` on hydrate). */
function createHeat() {
  const [events, setEvents] = createSignal<HeatEvent[]>([]);
  let counter = 0;

  const decayNow = () => {
    const fix = geolocation.latest();
    const pos: PlayerPos = fix ? { lat: fix.lat, lng: fix.lng } : null;
    setEvents((es) => (es.length === 0 ? es : tickDecay(es, Date.now(), pos)));
  };

  if (typeof window !== 'undefined') {
    setInterval(decayNow, HEAT_TICK_MS);
  }

  /** Add heat from a resolved hack. Records the origin so decay/future features
   *  can key on it. Clean (gain 0) is a no-op. */
  const gain = (band: HackBand, origin: { lat: number; lng: number }, tier: number): number => {
    const amount = heatGainFor(band, tier);
    if (amount <= 0) return 0;
    const now = Date.now();
    setEvents((es) => [
      ...es,
      { id: `h${counter++}`, amount, remaining: amount, origin, tier, lastTickAt: now },
    ]);
    return amount;
  };

  const value = createMemo(() => totalHeat(events()));
  const label = createMemo(() => heatLabel(value()));

  /** Reduce heat by `amount` (the reputation-management service). Drains the
   *  event list oldest-first; decay keeps working on what remains. Returns the
   *  amount actually cleared (capped by available heat). Earned-¤ only — the
   *  caller debits ¤; this must never be wired to a real-money purchase. */
  const reduce = (amount: number): number => {
    const cleared = Math.min(amount, value());
    if (cleared <= 0) return 0;
    setEvents((es) => drainHeat(es, cleared));
    return cleared;
  };

  return { value, label, events, gain, reduce };
}

export const heat = createRoot(() => createHeat());
